import { deepEqual } from 'fast-equals';

import { ItemSelector, Select2Item } from '../types';

export class SelectItemStrategy<Item extends Select2Item> extends ItemSelector<Item> {
  public selected: Item | null = null;

  private formControlValue: Item['value'];
  private preselected: Item | null = null;
  private items: Item[] | null;

  public select(item: Item) {
    this.selected = item;
    this.selectionChanged$.next();
  }

  public clear() {
    this.selected = null;
    this.selectionChanged$.next();
  }

  public isSelected(item: Item) {
    return !!this.selected && this.compareItemsFn(item, this.selected);
  }

  public remove() {
    this.clear();
  }

  public getValue(): Item['value'] {
    return this.selected?.value ?? null;
  }

  public setItems(items: Item[]) {
    this.items = items;
    if (!this.selected && this.formControlValue) {
      this.updateSelection();
    }
  }

  public getSelectedItem() {
    return this.selected ?? null;
  }

  public setPreselected(items: Item | null) {
    this.preselected = items;
    this.updateSelection();
  }

  public writeValue(value: Item['value']) {
    this.formControlValue = value;
    this.selected = null;
    this.updateSelection();
  }

  public setCompareItemsFn(fn: (a: Item, b: { value: Item['value'] } | Item) => boolean) {
    this.compareItemsFn = fn;
    this.updateSelection();
  }

  private compareItemsFn = (a: Item, b: Item | { value: Item['value'] }) =>
    deepEqual(a.value, b.value);

  private updateSelection() {
    const items = Array.isArray(this.items) ? this.items : [];
    const item = items.find(item => this.compareItemsFn(item, { value: this.formControlValue }));
    if (item) {
      this.selected = item;
      this.detectChanges$.next();
      return;
    }

    const isPreselected =
      !!this.preselected && this.compareItemsFn(this.preselected, { value: this.formControlValue });
    if (isPreselected) {
      this.selected = this.preselected;
      this.detectChanges$.next();
      return;
    }
  }
}
